/**
 * WidgetsSection - Sidebar section for widgets gallery
 *
 * Refactored to use the generic SidebarResourceSection component.
 * Displays all widgets, grouped by the user's preference (type, dashboard).
 * Note: Widgets don't support folders.
 */

import React, { useCallback } from "react";
import { FaLayerGroup } from "react-icons/fa";
import { useWidgets, WidgetWithContext } from "@/hooks/use-widgets";
import { useWidgetGroupBy } from "@/hooks/use-resource-group-by";
import { widgetGalleryConfig } from "@/config/resource-gallery-configs";
import { widgetSectionConfig } from "@/config/sidebar-section-configs";
import SidebarResourceSection, {
  SidebarGroupRenderProps,
  SidebarLoadingState,
} from "./sidebar-resource-section";
import SidebarGroupHeader from "./sidebar-group-header";
import WidgetItem from "./widget-item";

// =============================================================================
// Types
// =============================================================================

interface WidgetsSectionProps {
  className?: string;
  /** Callback when right-clicking on a selected item (for multi-select context menu) */
  onMultiSelectContextMenu?: (e: React.MouseEvent) => void;
}

// =============================================================================
// Component
// =============================================================================

const WidgetsSection: React.FC<WidgetsSectionProps> = ({
  className = "",
  onMultiSelectContextMenu,
}) => {
  const { widgets, selectedWidgetId, isLoading } = useWidgets();

  // Use shared hook for groupBy preference (synced with widgets gallery page)
  const { groupBy } = useWidgetGroupBy();

  // Render simple item
  const renderItem = useCallback(
    (props: {
      item: WidgetWithContext;
      isSelected: boolean;
      orderedIds: string[];
      onMultiSelectContextMenu?: (e: React.MouseEvent) => void;
    }) => (
      <WidgetItem
        key={props.item.id}
        widget={props.item}
        isActive={selectedWidgetId === props.item.id}
        isSelected={props.isSelected}
        orderedIds={props.orderedIds}
        onMultiSelectContextMenu={props.onMultiSelectContextMenu}
      />
    ),
    [selectedWidgetId]
  );

  // Custom group header with widget icons
  const renderGroupHeader = useCallback(
    (props: SidebarGroupRenderProps) => {
      // We need access to the items to get the icon, but the header doesn't have them
      // So we use a generic icon for groups
      const icon = <FaLayerGroup size={12} />;
      return (
        <SidebarGroupHeader
          groupKey={props.groupKey}
          icon={icon}
          count={props.itemCount}
          isExpanded={!props.isCollapsed}
          onToggle={props.onToggle}
          showCreateFolder={false}
        />
      );
    },
    []
  );

  if (isLoading) {
    return <SidebarLoadingState className={className} />;
  }

  return (
    <SidebarResourceSection<WidgetWithContext>
      items={widgets}
      config={widgetGalleryConfig}
      renderItem={renderItem}
      renderGroupHeader={renderGroupHeader}
      emptyMessage={widgetSectionConfig.emptyMessage}
      emptyIcon={<FaLayerGroup />}
      emptySubMessage={widgetSectionConfig.emptySubMessage}
      noResultsMessage={widgetSectionConfig.noResultsMessage}
      searchPlaceholder={widgetSectionConfig.searchPlaceholder}
      onMultiSelectContextMenu={onMultiSelectContextMenu}
      externalGroupBy={groupBy}
      className={className}
    />
  );
};

WidgetsSection.displayName = "WidgetsSection";

export default WidgetsSection;
